/* eslint-disable @typescript-eslint/no-explicit-any */
import { Route, Routes, useLocation } from 'react-router-dom';
import { ProtectedLayout } from './layout/ProtectedLayout';

import { UnprotectedLayout } from './layout/UnprotectedLayout';
import Login from './pages/login';
import Home from './pages/home';

// onboarding
import { OnboardingContainer } from './pages/on-boarding/container';
import OnBoardingStep1 from './pages/on-boarding/step-1';
import OnBoardingStep2 from './pages/on-boarding/step-2';
import OnBoardingStep3 from './pages/on-boarding/step-3';
import OnBoardingStep4 from './pages/on-boarding/step-4';
import OnBoardingStep5 from './pages/on-boarding/step-5';

import RegistrationForm from './pages/register';
import ResetPassword from './pages/reset-password/code';
import ForgotPassword from './pages/reset-password';

// RESOURCE
import ResourceHub from './pages/resource-hub/page';
import ResourcePage from './pages/resource-hub/resource';

// CHARACTER COMPASS
import CharacterCompassPage from './pages/character-compass';

// import ProfilePage from './pages/profile';

import NotfoundPage from './pages/not-found';
import { BookmarkPageScreen } from './pages/bookmarks';
// import withAuthGuard, { RoutesEnum } from './auth.guard';
import NotAuthorizedPage from './pages/not-authorized';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';

import AboutPageComponent from './pages/about-page';
import TermsAndConditionsPageComponent from './pages/terms-and-conditions-page';
import PrivacyPolicyPageComponent from './pages/privacy-policy-page';
import CopyrightPageComponent from './pages/copyright-page';
import CookiesPageComponent from './pages/cookies-page';
import { FeedbackPage } from './pages/learn/feedback';
import { MissionListing } from './pages/mission/listing';
import { MissionDetailingScreen } from './pages/mission/detailing';
import { ChapterScreen } from './pages/mission/chapter';
import { AssessmentScreen } from './pages/mission/assessment';
import { AssessmentPrePostScreen } from './pages/mission/assessment/components/assessment-pre-post-page';
import { CohortFormScreen } from './pages/mission/assessment/components/cohort-form-page';
import withAuthGuard, { RoutesEnum } from './auth.guard';
import { ReportingToolsScreen } from './pages/reporting-tools';

// COURSES

import Courses from './pages/learn/courses';
import CoursesInProgress from './pages/learn/in-progress';
import CoursesCompleted from './pages/learn/completed';
import Course from './pages/learn/course';
import Lesson from './pages/learn/course-lesson';
import { SearchScreen } from './pages/search';
import ProfilePage from './pages/profile';
import CalendarPageComponent from './pages/calendar';
import { isDevEnvironment } from './hooks/isDevelopment';
import RESPECTFrameworkPage from './pages/respect-framework';
import MeetTheCharactersPage from './pages/meet-the-characters';
import { ReflectionVideosScreen } from './pages/reflection-videos';
import { ManageStaffScreen } from './pages/manage-users';
import { AllDoneScreen } from './pages/mission/assessment/components/all-done-page';

export function Router() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (isDevEnvironment) {
      console.warn('GA has been disabled because this is a development environment.');
      return;
    }

    if (!import.meta.env.VITE_GA) {
      return;
    }

    ReactGA.initialize(import.meta.env.VITE_GA);
  }, []);

  return (
    <Routes>
      <Route path="/" element={<ProtectedLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/missions" element={withAuthGuard(<MissionListing />, RoutesEnum.MISSION)} />
        <Route path="/programmes" element={withAuthGuard(<MissionListing />, RoutesEnum.MISSION)} />
        <Route path="/mission/:missionSlug" element={withAuthGuard(<MissionDetailingScreen />, RoutesEnum.MISSION)} />
        <Route
          path="/mission/:missionSlug/chapter/:chapterTitle"
          element={withAuthGuard(<ChapterScreen />, RoutesEnum.MISSION)}
        />
        <Route
          path="/mission/:missionSlug/chapter/:chapterTitle/assessment"
          element={withAuthGuard(<AssessmentScreen />, RoutesEnum.MISSION)}
        />
        <Route
          path="/mission/:missionSlug/chapter/:chapterTitle/assessment/:cohortDocumentId"
          element={withAuthGuard(<AssessmentPrePostScreen />, RoutesEnum.MISSION)}
        />
        <Route
          path="/mission/:missionSlug/chapter/:chapterTitle/assessment/:cohortDocumentId/done"
          element={withAuthGuard(<AllDoneScreen />, RoutesEnum.MISSION)}
        />
        <Route
          path="/mission/:missionSlug/chapter/:chapterTitle/cohort/create"
          element={withAuthGuard(<CohortFormScreen />, RoutesEnum.MISSION)}
        />
        <Route
          path="/mission/:missionSlug/chapter/:chapterTitle/cohort/:cohortDocumentId/edit"
          element={withAuthGuard(<CohortFormScreen />, RoutesEnum.MISSION)}
        />

        <Route path="/character-compass" element={<CharacterCompassPage />} />
        <Route path="/reporting-tools" element={withAuthGuard(<ReportingToolsScreen />, RoutesEnum.REPORTING_TOOLS)} />
        <Route path="/manage-staff" element={withAuthGuard(<ManageStaffScreen />, RoutesEnum.MANAGE_STAFF)} />

        <Route path="/resource-hub" element={withAuthGuard(<ResourceHub />, RoutesEnum.RESOURCE_HUB)} />
        <Route path="/resource-hub/type/:type" element={withAuthGuard(<ResourceHub />, RoutesEnum.RESOURCE_HUB)} />
        <Route path="/resource-hub/resource/:slug" element={withAuthGuard(<ResourcePage />, RoutesEnum.RESOURCE_HUB)} />

        <Route path="/learn" element={withAuthGuard(<Courses />, RoutesEnum.LEARN)} />
        <Route path="/learn/in-progress" element={withAuthGuard(<CoursesInProgress />, RoutesEnum.LEARN)} />
        <Route path="/learn/completed" element={withAuthGuard(<CoursesCompleted />, RoutesEnum.LEARN)} />
        <Route path="/learn/course/:slug" element={withAuthGuard(<Course />, RoutesEnum.LEARN)} />
        <Route
          path="/learn/course/:slug/module/:idModule/lesson/:idLesson"
          element={withAuthGuard(<Lesson />, RoutesEnum.LEARN)}
        />
        <Route path="/learn/course/:slug/feedback" element={withAuthGuard(<FeedbackPage />, RoutesEnum.LEARN)} />
        <Route path="/search" element={withAuthGuard(<SearchScreen />, RoutesEnum.SEARCH)} />

        <Route path="/profile" element={<ProfilePage />} />

        <Route path="/bookmarks" element={withAuthGuard(<BookmarkPageScreen />, RoutesEnum.BOOKMARK)} />

        <Route path="/calendar" element={<CalendarPageComponent />} />
        <Route path="/calendar/:month" element={<CalendarPageComponent />} />

        <Route element={<OnboardingContainer />}>
          <Route path="/on-boarding/step-1" element={<OnBoardingStep1 />} />
          <Route path="/on-boarding/step-2" element={<OnBoardingStep2 />} />
          <Route path="/on-boarding/step-3" element={<OnBoardingStep3 />} />
          <Route path="/on-boarding/step-4" element={<OnBoardingStep4 />} />
          <Route path="/on-boarding/step-5" element={<OnBoardingStep5 />} />
        </Route>

        <Route path="/about" element={<AboutPageComponent />} />
        <Route path="/respect-framework" element={<RESPECTFrameworkPage />} />
        <Route path="/meet-the-characters" element={<MeetTheCharactersPage />} />
        <Route path="/cookies" element={<CookiesPageComponent />} />

        <Route path="/terms-and-conditions" element={<TermsAndConditionsPageComponent />} />
        <Route path="/not-authorized" element={<NotAuthorizedPage />} />
        <Route path="/reflection-videos" element={<ReflectionVideosScreen />} />
        <Route path="/reflection-videos/:keyStageDocumentId" element={<ReflectionVideosScreen />} />
      </Route>
      <Route element={<UnprotectedLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/login/confirmed" element={<Login />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:code" element={<ResetPassword />} />
        <Route path="/register" element={<RegistrationForm />} />
        <Route path="/register/:code" element={<RegistrationForm />} />
        <Route path="/copyright" element={<CopyrightPageComponent />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPageComponent />} />
      </Route>
      <Route path="*" element={<NotfoundPage />} />
    </Routes>
  );
}
